// "The bouquet": a summer afternoon by an open door, friends arriving → each tap brings a stem, little hearts bloom → Van Gogh light → her painting emerges.
export const bouquet = {
  slug: "the-bouquet",
  duration: 25,
  music: "waltz-op64-2",
  musicAt: 6,
  captionTail: "Just a simple wish to paint how beautiful the flowers were.",
  stages: [
    { id: "summer", at: 0, hint: "a summer afternoon" },
    { id: "gifts", at: 6, hint: "tap to receive a flower" },
    { id: "vangogh", at: 15.5, hint: "Van Gogh light" },
    { id: "settle", at: 21, hint: "" },
  ],
  touch: { kind: "gift", from: 6, until: 15.5 },
  // painted panorama: a summer afternoon by the open door, golden swirling light
  layers: [
    { src: "/arcs/bouquet/door.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, glow: 0.15, opacity: [[0, 1], [6, 0.72], [15.5, 0.72], [17, 1]], show: [-1, 0, 99, 99] },
  ],
  autoGifts: [7, 9.5, 12, 14.2],
  line: { text: "Flowers are summer's sweetest gift.", from: 17, to: 31, pos: "low" },
  cues: [[1.2, "laugh"], [3.4, "laugh"], [5.2, "laugh"]],
  ambient: { scene: "angan", until: 99, then: [[15.5, "garden"]] },
  look: { start: 0.08, lift: [0, 1], end: 0.08, yaw: [[0, -0.5], [4, 0.3], [6, 0], [25, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "bouquet", pos: "low" },
  speed: [[0, 1], [25, 1]],
  reveal: {
    style: "flow",
    from: "bottom",
    duration: 10,
    focus: [
      { rect: [0.25, 0.27, 0.35, 0.25], style: "radial" },
      { rect: [0.6, 0.2, 0.32, 0.25], style: "radial" },
      { rect: [0.45, 0.45, 0.35, 0.25], style: "radial" },
      { rect: [0.32, 0.0, 0.4, 0.27], style: "radial" },
    ],
  },
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform vec4 uMark[8];
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface;
varying vec3 vPos;
${"__NOISE__"}
float hash1(float n){ return fract(sin(n * 91.7) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
float seg(vec2 p, vec2 a, vec2 b){ vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }
float heart(vec2 q){ q.y -= 0.25 * abs(q.x) * 0.0; float x = q.x, y = q.y + 0.25; float a = x * x + y * y - 1.0; return a * a * a - x * x * y * y * y; }
// thick, short, swirling strokes in his spirit
vec3 vangogh(vec2 P){
  float a = atan(P.y - 3.0, P.x) + 1.57 + snoise(vec3(P * 0.35, 1.0)) * 1.6;
  vec2 r = mat2(cos(a), -sin(a), sin(a), cos(a)) * P;
  vec2 c = r / vec2(0.2, 0.07);
  vec2 f = fract(c) - 0.5;
  float h = hash2(floor(c));
  float dab = 1.0 - smoothstep(0.3, 0.48, length(f * vec2(1.0, 1.4)));
  vec3 base = mix(vec3(0.24, 0.18, 0.24), vec3(0.34, 0.3, 0.36), h);
  vec3 acc = h > 0.9 ? vec3(0.85, 0.7, 0.3) : h > 0.8 ? vec3(0.35, 0.4, 0.7) : h > 0.72 ? vec3(0.3, 0.45, 0.32) : base;
  return mix(base * 0.8, acc, dab * (0.6 + 0.4 * h));
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float n2 = snoise(vPos * 3.0 + 4.0) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float vg = smoothstep(15.5, 18.5, uT);
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    vec2 P = vec2(X, vPos.y);
    // a sunny room: warm cream walls, sunlight falling in slanted panes
    col = mix(vec3(0.96, 0.88, 0.7), vec3(0.86, 0.74, 0.56), smoothstep(0.0, 6.0, P.y)) * (0.94 + 0.08 * n2);
    float pane = step(0.5, fract((P.x + P.y * 0.6) * 0.35)) * step(fract(P.y * 0.5 + P.x * 0.1), 0.8);
    col += vec3(0.25, 0.18, 0.05) * pane * 0.4 * step(0.8, abs(P.x));
    // the open door, the summer garden outside
    vec2 dq = P - vec2(0.0, 1.7);
    float door = step(abs(dq.x), 0.7) * step(abs(dq.y), 1.3);
    float frame = step(abs(dq.x), 0.8) * step(abs(dq.y), 1.4) * (1.0 - door);
    vec3 garden = mix(vec3(0.62, 0.82, 0.98), vec3(0.95, 0.97, 1.0), smoothstep(1.4, 3.0, P.y));
    float bush = smoothstep(1.3 + 0.25 * snoise(vec3(P.x * 3.0, 0.0, 2.0)), 1.2, P.y);
    garden = mix(garden, mix(vec3(0.25, 0.55, 0.22), vec3(0.98, 0.6, 0.2), step(0.85, hash2(floor(P * 18.0)))), bush);
    col = mix(col, garden, door);
    col = mix(col, vec3(0.98, 0.97, 0.94), frame);
    // the light of the room turns to thick swirling strokes, the bouquet glowing against plum-grey
    col = mix(col, vangogh(P), vg);
    // the table and a blue-glazed vase
    float table = step(abs(P.x), 1.6) * step(abs(P.y - 0.66), 0.1);
    col = mix(col, mix(vec3(0.62, 0.42, 0.25), vec3(0.32, 0.22, 0.2), vg) * (0.85 + 0.2 * n2), table);
    vec2 vq = P - vec2(0.0, 0.95);
    float vase = step(abs(vq.x), 0.13 + 0.05 * sin((vq.y + 0.2) * 7.0)) * step(abs(vq.y), 0.2);
    col = mix(col, mix(vec3(0.3, 0.45, 0.75), vec3(0.45, 0.6, 0.9), smoothstep(0.1, -0.1, vq.x)), vase);
    col += vec3(1.0, 0.8, 0.5) * exp(-length((P - vec2(0.0, 1.6)) * vec2(1.0, 0.9)) * 1.8) * vg * 0.35;
    // stems brought in through the doorway, gathering into a bouquet; a little heart blooms by each
    for (int i = 0; i < 8; i++){
      float t0 = uMark[i].w;
      if (t0 <= 0.0) continue;
      float fi = float(i);
      float k = smoothstep(t0, t0 + 1.4, uT);
      float ang = (mod(fi * 5.0, 8.0) - 3.5) * 0.17;
      vec2 dir = vec2(sin(ang), cos(ang));
      float len = 0.6 + 0.25 * hash1(fi + 3.0);
      vec2 base = mix(vec2(0.25 - 0.1 * fi * 0.0, 1.9), vec2(0.0, 1.12), k);
      vec2 head = base + dir * len + vec2(0.0, 0.3 * sin(k * 3.1416));
      col = mix(col, vec3(0.2, 0.45, 0.18), 1.0 - smoothstep(0.008, 0.016, seg(P, base, head)));
      vec2 q = P - head;
      float rr = length(q);
      float pa = atan(q.y, q.x) + fi;
      float petal = 1.0 - smoothstep(0.92, 1.0, rr / (0.13 * (0.45 + 0.55 * pow(abs(cos(pa * 5.0)), 0.6))));
      float ci = mod(fi, 3.0);
      vec3 pc = ci < 0.5 ? vec3(0.95, 0.38, 0.15) : ci < 1.5 ? vec3(0.98, 0.92, 0.55) : vec3(0.72, 0.7, 0.95);
      col = mix(col, pc * (0.85 + 0.25 * cos(pa * 10.0)), petal);
      col = mix(col, vec3(0.85, 0.25, 0.12), 1.0 - smoothstep(0.028, 0.035, rr));
      vec2 hq = (P - head - vec2(0.16 * (hash1(fi) - 0.5) * 2.0, -0.22)) / (0.045 * smoothstep(t0 + 1.2, t0 + 2.0, uT) + 0.0001);
      col = mix(col, vec3(0.88, 0.12, 0.18), step(heart(vec2(hq.x, hq.y)), 0.0) * step(0.001, smoothstep(t0 + 1.2, t0 + 2.0, uT)));
    }
    local = vec2(X, P.y - uCy);
  } else if (uSurface < 1.5) {
    vec2 p = vPos.xz;
    col = mix(mix(vec3(0.66, 0.48, 0.3), vec3(0.58, 0.4, 0.25), step(0.5, fract(p.x * 2.0))) * (0.9 + 0.15 * n), vangogh(p * 1.3 + 9.0) * 0.8, vg);
    local = vPos.xz;
  } else {
    col = mix(vec3(0.97, 0.92, 0.8), vangogh(vPos.xz + 4.0) * 0.7, vg);
    local = vPos.xz;
  }
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
